/**
 * Portal readiness signal (P4 fix).
 *
 * WHY THIS EXISTS. The PDF pipeline captured the portal's "Loading…" state: it
 * waited on networkidle2, which fires when HTTP connections settle — NOT when
 * React has received the payload and painted it. Those are different moments,
 * and which one wins is a race decided by device speed and network latency.
 * That is why it rendered correctly on a phone and not on a PC.
 *
 * A longer timeout would not fix this. It would make the race rarer and
 * therefore harder to reproduce, while leaving a PDF of the word "Loading…"
 * possible on any unlucky request. So the page states, explicitly, when it is
 * done, and the renderer waits for that statement rather than guessing.
 *
 * TWO ATTRIBUTES on <html>:
 *   data-portal-ready="1"        the page has SETTLED and painted
 *   data-portal-state="ok|error" what it settled INTO
 *
 * Both, not just the first: an error page settles too, and a renderer that only
 * waited for "ready" would happily produce a PDF of "This link is no longer
 * available". pdf.ts checks the state and refuses.
 */

import { useEffect } from 'react';

export const PORTAL_READY_ATTR = 'data-portal-ready';
export const PORTAL_STATE_ATTR = 'data-portal-state';

export type PortalSettleState = 'loading' | 'ok' | 'error';

/**
 * Signal readiness once the view has rendered real data.
 *
 * The double requestAnimationFrame is load-bearing. Setting the flag inside the
 * effect would announce "ready" while this commit is still unpainted — the
 * first rAF runs before the paint, the second after it. Only then is what the
 * renderer captures guaranteed to be what the user would see.
 *
 * AND FONTS, which is the same bug wearing a different hat. The document sets
 * typed signatures in a self-hosted webfont ('BVL Signature'), and a webfont
 * loads asynchronously: React can paint, both frames can pass, and the flag can
 * go up while the signature is still showing in the fallback face. The PDF
 * renderer waits for exactly this flag, so the result would be a contract whose
 * signature is set in the wrong typeface — intermittently, depending on whether
 * the font beat the paint, which is precisely the kind of race that reproduces
 * on nobody's machine.
 *
 * document.fonts.ready settles when font loading FINISHES, successfully or not,
 * so a font that 404s delays the signal by the load attempt and no longer. It is
 * awaited before the frames rather than after, because applying a font reflows
 * the text and the two rAFs must measure the final layout.
 */
export function usePortalReady(state: PortalSettleState): void {
  useEffect(() => {
    const root = document.documentElement;
    if (state === 'loading') {
      root.removeAttribute(PORTAL_READY_ATTR);
      root.setAttribute(PORTAL_STATE_ATTR, 'loading');
      return;
    }

    let cancelled = false;
    let raf = 0;

    // Older engines without the Font Loading API resolve immediately; the
    // double-rAF below is then exactly the behaviour this hook always had.
    const fontsSettled: Promise<unknown> = (document as any).fonts?.ready ?? Promise.resolve();

    fontsSettled.catch(() => { /* a font that failed to load must not block the signal */ })
      .then(() => {
        if (cancelled) return;
        raf = requestAnimationFrame(() => {
          raf = requestAnimationFrame(() => {
            if (cancelled) return;
            root.setAttribute(PORTAL_STATE_ATTR, state);
            root.setAttribute(PORTAL_READY_ATTR, '1');
          });
        });
      });

    return () => { cancelled = true; if (raf) cancelAnimationFrame(raf); };
  }, [state]);
}
